import { Component, inject, OnInit, ViewChild, ElementRef } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { client } from '@passwordless-id/webauthn' 
import { server } from '@passwordless-id/webauthn' 
import { RegistrationEncoded, RegistrationParsed } from '@passwordless-id/webauthn/dist/esm/types';

import { LoginVm } from '../../../shared/generated/Models/LoginVm';
import { SecurityService } from '../../../core/security/security.service';
import { SystemMessageService, SystemMessage } from '../../../core/system-message/system-message-service';
import { validateForm } from '../../../shared/form-elements/form-validateForm.function';
import { CachePrimerService } from '../../../services/service-workers/cache-primer.service';
import { WebAuthnService } from '../../security/multi-factor-auth/web-authn.service';
import { MultiFactorAuthModalComponent } from './multi-factor-auth-modal/multi-factor-auth-modal.component';
import { CheckNetworkService } from '../../../../local-db/services/check-network.service';

@Component({
    selector: 'pcg-login'
    , templateUrl: './login.component.html'
    , styleUrls: ['./login.component.scss']
    , providers: [
        {
            provide: NG_VALUE_ACCESSOR
            , multi: true
            , useExisting: LoginComponent
        }
    ],
    standalone: false
})
export class LoginComponent implements OnInit {

    title = inject(Title);
    private route = inject(ActivatedRoute);
    private ms = inject(SystemMessageService);
    private sec = inject(SecurityService);
    private router = inject(Router);
    private cachePrimerService = inject(CachePrimerService);
    private modalService = inject(NgbModal);
    private webAuthn = inject(WebAuthnService);

	@ViewChild('myForm', { static: true }) myForm: ElementRef;

	loginForm = LoginVm.Form;
	sm: SystemMessage;

	isClientAvailable = false; //client.isAvailable();
	isLocalAuthenticator = false;
	isLogin = false;
	isError = false;
	isOnline: boolean;
	errorString = "There was a problem registering your passwordless signin credentials. ";

	async ngOnInit(): Promise<void> {
        this.title.setTitle('Login');
		this.isOnline = CheckNetworkService.checkNetworkStatus();
		this.isLocalAuthenticator = await client.isLocalAuthenticator();
		const user = this.sec.getUser();
		if (user) { this.loginRedirect(); }
		else if (localStorage.getItem('webAuthn') !== null) { await this.passwordlessLogin(); }
	}

	async onSubmit(): Promise<void> {
		if (this.loginForm.valid) {
			this.sec.setLocalStorage('lastAccess', new Date());
			this.ms.getHttpObservable(
				this
				, 'api/Account/Login'
				, this.loginForm
			).subscribe(async msg => {
				this.sm = msg;
				const val = msg.value;
				if (
					msg.isSuccessful 
					&& this.isClientAvailable 
					&& val
				) {						
					if (localStorage.getItem('webAuthnRemember') === null) { this.isLogin = true; } 
					else if (localStorage.getItem('webAuthnRemember') === "false") { this.doLogin(); } 
					else { this.doLogin(); }
				} else if (!msg.isSuccessful) {
					this.isError = true;
					this.errorString = msg.message!;
				} else { this.doLogin(); }
			}); 
		} else { validateForm(this.loginForm); }
	}

	doLogin() {
		const val = this.sm.value;
		if (!val) { return; }
		if (val.hasTwoFactorAuth) {
			// Need this hack to avoid change detection error on modal open
			this.myForm.nativeElement.ownerDocument.activeElement.blur();
			// Open the 2FA modal
			this.modalService.open(MultiFactorAuthModalComponent);
			// Handle 2FA
			this.doTwoFactorAuth();
			return;
		}
		this.loginForm.reset();
		this.sec.setSecurity(val.token, val.user, val.security);
		this.loginRedirect();
	}

	async registerPasswordlessLogin(msg: SystemMessage): Promise<void>  {
		const val = msg.value;
		let registrationParsed: RegistrationParsed;
		let registration: RegistrationEncoded;
		let challenge = "";
		
		try {
			challenge = val.challenge;
			registration = await client.register(
				this.loginForm.controls.userName.value
				, challenge
				, {
					authenticatorType: "auto"
					, userVerification: "required"
					, timeout: 60000
					, attestation: false
					, userHandle: "recommended to set it to a random 64 bytes value"
					, debug: false
				}
			);
			const expected = {
				challenge: challenge // whatever was randomly generated by the server
				, origin: val.baseUrl
			};
			registrationParsed = await server.verifyRegistration(registration, expected);
			
			// Store webAuthn in local storage
			if (registrationParsed === null) { localStorage.removeItem("webAuthn"); } 
			else { localStorage.setItem("webAuthn", JSON.stringify(registrationParsed)); }

			this.doLogin();
		} catch (ex: any) {
			this.isError = true;
			this.errorString += ex.message;
		}
	}

	async passwordlessLogin(): Promise<void> {
		if (localStorage.getItem('webAuthn') !== null) {
			try {
				let registrationParsed = JSON.parse(localStorage.getItem('webAuthn')!);
				let clientParsed = registrationParsed?.client;
				let challenge = clientParsed?.challenge;

				if (
					challenge !== "" 
					&& challenge !== null 
					&& challenge !== undefined
				) {
					let res = await client.authenticate([], challenge);
					if (res !== null) {
						const webAuthn = JSON.parse(localStorage.getItem('webAuthn')!);
						this.loginForm.controls.userName.patchValue(webAuthn.username);
						this.loginForm.controls.concurrencyStamp.patchValue(challenge);
						this.loginForm.controls.password.patchValue(webAuthn.username);
						this.ms.getHttpObservable(this, 'api/Account/Login', this.loginForm)
							.subscribe(async msg => { 
								this.sm = msg;
								this.doLogin();
							}
						);
					}
				}
			} catch (ex: any) { }			
		}
	}

	async yesClick(): Promise<void> { await this.registerPasswordlessLogin(this.sm); }

	noClick() {
		localStorage.removeItem("webAuthnRemember");
		localStorage.setItem("webAuthnRemember", "false");
		this.doLogin(); 
	}

	loginRedirect() {
		this.cachePrimerService.primeCache();
		let redirectUrl = this.route.snapshot.queryParamMap.get('redirectUrl') || sessionStorage.getItem('redirectUrl');
		if (redirectUrl) {
			const baseUrl = document.getElementsByTagName('base')[0].href;
			redirectUrl = redirectUrl.replace(/^\//, '');
			if (redirectUrl.startsWith('uploads/')) { window.location.href = baseUrl + redirectUrl; }
			else {
				this.router.navigateByUrl(redirectUrl);
				sessionStorage.removeItem('redirectUrl');
			}
		} else { this.router.navigate(['/dashboard']); }
	}

	doTwoFactorAuth() {
		if (!this.webAuthn.isFidoCompatible()) {
			this.modalService.dismissAll();
			return;
		}

		// Save user credentials and clear form
		const userName = this.loginForm.value.userName;
		const password = this.loginForm.value.password;
		this.loginForm.reset();

		// Get assertion options from the server for user
		this.webAuthn.getAssertionOptions(userName, password)
			.subscribe(makeAssertionOptions => {
				navigator['credentials'].get({ publicKey: makeAssertionOptions }).then((credential) => {
					this.sec.setLocalStorage('lastAccess', new Date());
					this.webAuthn.verifyAssertion(
						credential
						, userName
						, password
					).subscribe((msg: SystemMessage) => {
						this.sm = msg;
						this.modalService.dismissAll();
						this.doLogin();
					});
				}).catch((err) => {
					this.modalService.dismissAll();
					// Do this so there is a failed login history rercord:
					this.webAuthn.verifyAssertion(
						null
						, userName
						, password
					).subscribe((msg: SystemMessage) => {
						this.sm = msg;
						this.doLogin();
					});
				});
			}
		);
	}
}
